import { useCallback, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceDot,
} from 'recharts';
import type { Props as RechartsLabelProps } from 'recharts/types/component/Label';

type TempTickProps = {
  x?: string | number;
  y?: string | number;
  payload?: { value: number };
  fill?: string;
  fontSize?: number;
};

const TempTick = ({ x = 0, y = 0, payload, fill, fontSize = 14 }: TempTickProps) => {
  const value = payload?.value;
  if (value === undefined || value === null || !Number.isFinite(value)) return null;
  return (
    <text x={x} y={y} textAnchor="end" fill={fill ?? '#666'} fontSize={fontSize} dy={4}>
      {value}
    </text>
  );
};

type ReferenceDotShapeProps = {
  cx?: number;
  cy?: number;
};

interface RoastLineChartProps {
  variant: 'inline' | 'modal';
  isMobile: boolean;
  chartData: {
    time: number;
    bean: number | null;
    ror: number | null;
    fire: number | null;
    airflow: number | null;
    airTemp: number | null;
    drumTemp: number | null;
    environmentTemp: number | null;
    drumRpm: number | null;
  }[];
  phaseAreas: { x1: number; x2: number; fill: string; opacity: number; label: string; detail?: string }[];
  xTicks: number[];
  tempAxisMax: number;
  tempTicks: number[];
  rightAxisMax: number;
  rightTicks: number[];
  hasAirTempData: boolean;
  hasDrumTempData: boolean;
  hasEnvironmentTempData: boolean;
  hasDrumRpmData: boolean;
  stageMarkers?: { key: string; time: number; temp: number; labelTop: string; labelBottom: string }[];
}

/**
 * The recharts roast-profile chart (axes, line series, phase areas and stage markers).
 * `variant` selects the inline-card vs zoom-modal sizing; the series/areas/markers are
 * identical between the two, so both layouts share this single implementation.
 */
export function RoastLineChart({
  variant,
  isMobile,
  chartData,
  phaseAreas,
  xTicks,
  tempAxisMax,
  tempTicks,
  rightAxisMax,
  rightTicks,
  hasAirTempData,
  hasDrumTempData,
  hasEnvironmentTempData,
  hasDrumRpmData,
  stageMarkers,
}: RoastLineChartProps) {
  const isModal = variant === 'modal';

  const renderStepLabel = useCallback((props: RechartsLabelProps, dataKey: keyof RoastLineChartProps['chartData'][number], color: string) => {
    const { x, y, value, index } = props;
    const idx = typeof index === 'number' ? index : 0;
    const numericValue = typeof value === 'number' && Number.isFinite(value) ? value : null;
    if (numericValue === null) return null;

    let isChanged = false;
    let isDrop = false;
    let isFirstValid = false;

    let lastValidValue = null;
    for (let i = idx - 1; i >= 0; i--) {
      const val = chartData[i]?.[dataKey];
      if (val !== null && val !== undefined) {
        lastValidValue = val;
        break;
      }
    }

    if (lastValidValue === null) {
      isFirstValid = true;
      isChanged = true;
    } else {
      if (numericValue !== lastValidValue) {
        isChanged = true;
        if (numericValue < lastValidValue) {
          isDrop = true;
        }
      }
    }

    let isLastValid = false;
    let hasNextValid = false;
    for (let i = idx + 1; i < chartData.length; i++) {
      const val = chartData[i]?.[dataKey];
      if (val !== null && val !== undefined) {
        hasNextValid = true;
        break;
      }
    }
    if (!hasNextValid) {
      isLastValid = true;
    }

    if (isChanged || isLastValid) {
      let dx = 0;
      let dy = 0;
      let textAnchor: "start" | "middle" | "end" = "middle";

      if (isFirstValid) {
        dx = -5;
        dy = 4;
        textAnchor = "end";
      } else if (isLastValid) {
        dx = 5;
        dy = 4;
        textAnchor = "start";
      } else if (isDrop) {
        dx = 4;
        dy = 14;
        textAnchor = "start";
      } else {
        dx = 4;
        dy = -6;
        textAnchor = "start";
      }

      return (
        <text x={x} y={y} dx={dx} dy={dy} fill={color} fontSize={isMobile ? 8 : 11} textAnchor={textAnchor} fontWeight="bold">
          {numericValue}
        </text>
      );
    }
    return null;
  }, [chartData, isMobile]);

  const renderFireLabel = useCallback(
    (props: RechartsLabelProps) => renderStepLabel(props, 'fire', '#dc2626'),
    [renderStepLabel]
  );
  const renderAirflowLabel = useCallback(
    (props: RechartsLabelProps) => renderStepLabel(props, 'airflow', '#2563eb'),
    [renderStepLabel]
  );

  const makePhaseAreaLabelContent = useCallback(
    (area: RoastLineChartProps['phaseAreas'][number]) => (props: RechartsLabelProps) => {
      const viewBox = props.viewBox;
      const boxX = typeof props.x === 'number' ? props.x : viewBox && 'x' in viewBox ? viewBox.x : 0;
      const boxY = typeof props.y === 'number' ? props.y : viewBox && 'y' in viewBox ? viewBox.y : 0;
      const boxW = typeof props.width === 'number' ? props.width : viewBox && 'width' in viewBox ? viewBox.width : 0;
      const x = boxX + boxW / 2;
      const y = boxY + (isMobile ? 8 : 14);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      const labelFont = isMobile ? 7.5 : 12;
      const detailFont = isMobile ? 6.5 : 11;
      return (
        <text x={x} y={y} textAnchor="middle" fill="#666">
          <tspan x={x} dy={0} fontSize={labelFont} fontWeight={600}>
            {area.label}
          </tspan>
          {area.detail ? (
            <tspan x={x} dy={isMobile ? 8 : 14} fontSize={detailFont} fontFamily="monospace" fontWeight={700}>
              {area.detail}
            </tspan>
          ) : null}
        </text>
      );
    },
    [isMobile]
  );

  const makeMarkerShape = useCallback(
    (marker: NonNullable<RoastLineChartProps['stageMarkers']>[number], markerVariant: 'main' | 'modal') =>
      (props: ReferenceDotShapeProps) => {
        const { cx, cy } = props;
        if (cx === undefined || cy === undefined || isNaN(cx) || isNaN(cy)) return <g />;
        const dx = isMobile ? 6 : 15;
        const dyTop = isMobile ? 15 : 35;
        const dyBottom = isMobile ? 15 : 35;
        const fontSize = isMobile ? 8 : markerVariant === 'modal' ? 13 : 12;
        const radius = isMobile ? 2.5 : markerVariant === 'modal' ? 4 : 3.5;
        const strokeWidth = markerVariant === 'modal' ? 1.5 : 1;
        return (
          <g>
            <circle cx={cx} cy={cy} r={radius} fill="#111" stroke="#fff" strokeWidth={strokeWidth} />
            {marker.labelTop && (
              <g>
                <path d={`M${cx},${cy} L${cx + dx},${cy - dyTop}`} stroke="#111" strokeWidth={1} fill="none" />
                <text x={cx + dx} y={cy - dyTop - 3} fill="#111" fontSize={fontSize} textAnchor="middle">
                  {marker.labelTop}
                </text>
              </g>
            )}
            {marker.labelBottom && (
              <g>
                <path d={`M${cx},${cy} L${cx + dx},${cy + dyBottom}`} stroke="#111" strokeWidth={1} fill="none" />
                <text x={cx + dx} y={cy + dyBottom + fontSize} fill="#111" fontSize={fontSize} textAnchor="middle">
                  {marker.labelBottom}
                </text>
              </g>
            )}
          </g>
        );
      },
    [isMobile]
  );

  const timeTickFormatter = useCallback((val: number) => {
    const m = Math.floor(val);
    const s = Math.round((val - m) * 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, []);

  const xDomain = useMemo<[number, (max: number) => number]>(
    () => [0, (max: number) => Math.max(max, xTicks[xTicks.length - 1])],
    [xTicks]
  );

  // Shared line series — identical in both the inline and modal charts.
  const renderSeries = () => (
    <>
      <Line
        yAxisId="left"
        type="monotone"
        dataKey="bean"
        stroke="#16a34a"
        dot={false}
        name="Bean Temp"
        strokeWidth={2}
        connectNulls
        isAnimationActive={false}
      />

      <Line
        yAxisId="right"
        type="monotone"
        dataKey="ror"
        stroke="#eab308"
        dot={false}
        name="ROR"
        strokeWidth={2}
        connectNulls
        isAnimationActive={false}
      />

      <Line
        yAxisId="left"
        type="stepAfter"
        dataKey="fire"
        stroke="#dc2626"
        dot={false}
        name="Fire"
        strokeWidth={2}
        connectNulls
        isAnimationActive={false}
        label={renderFireLabel}
      />
      <Line
        yAxisId="left"
        type="stepAfter"
        dataKey="airflow"
        stroke="#2563eb"
        dot={false}
        name="Airflow"
        strokeWidth={2}
        connectNulls
        isAnimationActive={false}
        label={renderAirflowLabel}
      />
      {hasAirTempData && (
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="airTemp"
          stroke="#0ea5e9"
          dot={false}
          name="Air Temp"
          strokeWidth={2}
          connectNulls
          isAnimationActive={false}
        />
      )}
      {hasDrumTempData && (
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="drumTemp"
          stroke="#78350f"
          dot={false}
          name="Drum Temp"
          strokeWidth={2}
          connectNulls
          isAnimationActive={false}
        />
      )}
      {hasEnvironmentTempData && (
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="environmentTemp"
          stroke="#a855f7"
          dot={false}
          name="Environment Temp"
          strokeWidth={2}
          connectNulls
          isAnimationActive={false}
        />
      )}
      {hasDrumRpmData && (
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="drumRpm"
          stroke="#f97316"
          dot={false}
          name="Drum RPM"
          strokeWidth={2}
          connectNulls
          isAnimationActive={false}
        />
      )}
    </>
  );

  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
      <LineChart
        data={chartData}
        margin={isModal ? { top: 5, right: isMobile ? 0 : 30, left: isMobile ? 0 : 30, bottom: 70 } : { top: 5, right: 5, left: 5, bottom: 30 }}
      >
        {phaseAreas.map((area, index) => (
          <ReferenceArea
            key={`phase-${index}`}
            x1={area.x1}
            x2={area.x2}
            yAxisId="left"
            fill={area.fill}
            fillOpacity={area.opacity}
            label={{
              position: 'insideTop',
              content: makePhaseAreaLabelContent(area),
            }}
          />
        ))}

        <CartesianGrid strokeDasharray="3 3" />

        <XAxis
          dataKey="time"
          type="number"
          ticks={xTicks}
          padding={{ left: 10, right: 10 }}
          domain={xDomain}
          tickFormatter={timeTickFormatter}
          label={{ value: 'Time', position: 'insideBottom', offset: isModal ? -5 : 0 }}
          tick={{ fontSize: isModal ? (isMobile ? 9 : 12) : (isMobile ? 9 : 10), textAnchor: 'end' }}
          angle={isModal ? -35 : -45}
          tickMargin={isModal ? (isMobile ? 8 : 14) : 8}
          height={isModal ? (isMobile ? 50 : 80) : 50}
        />

        <YAxis
          yAxisId="left"
          domain={[-5, tempAxisMax]}
          ticks={tempTicks}
          interval={0}
          minTickGap={0}
          padding={{ top: 6, bottom: 6 }}
          tick={<TempTick fontSize={isMobile ? 9 : 14} />}
          width={isModal ? (isMobile ? 22 : 60) : (isMobile ? 22 : 35)}
          label={
            isModal
              ? isMobile
                ? undefined
                : {
                    value: 'Temp (°C)',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 0,
                    style: { textAnchor: 'middle', fontSize: 12 },
                  }
              : {
                  value: 'Temp (°C)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 0,
                  className: 'hidden sm:block',
                  style: { textAnchor: 'middle', fontSize: 12 },
                }
          }
        />

        <YAxis
          yAxisId="right"
          orientation="right"
          domain={[0, rightAxisMax]}
          ticks={rightTicks}
          width={isModal ? (isMobile ? 20 : 60) : (isMobile ? 20 : 30)}
          tick={{ fontSize: isMobile ? 9 : 12 }}
          label={
            isModal
              ? isMobile
                ? undefined
                : {
                    value: 'ROR',
                    angle: 90,
                    position: 'insideRight',
                    offset: 0,
                    style: { textAnchor: 'middle', fontSize: 12 },
                  }
              : {
                  value: 'ROR & Drum Temp',
                  angle: 90,
                  position: 'insideRight',
                  offset: 0,
                  className: 'hidden sm:block',
                  style: { textAnchor: 'middle', fontSize: 12 },
                }
          }
        />

        <Tooltip labelFormatter={(label) => timeTickFormatter(Number(label))} />

        {renderSeries()}

        {stageMarkers?.map((marker, index) => (
          <ReferenceDot
            key={`marker-${index}`}
            x={marker.time}
            y={marker.temp}
            yAxisId="left"
            shape={makeMarkerShape(marker, isModal ? 'modal' : 'main')}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
